import { useEffect, useState } from "react";
import {
    checkGoogleCalendarEnabledStatus,
    checkGoogleCalendarIntegration,
    checkMicrosoftCalendarEnabledStatus,
    checkMicrosoftCalendarIntegration,
    checkMicrosoftTokenValidity,
    checkTokenValidity,
} from "../services.js";

/**
 * Custom hook for managing Google and Microsoft calendar sync
 */
export const useCalendarSync = ({ token, user }) => {
    const [isGoogleSyncVisible, setIsGoogleSyncVisible] = useState(false);
    const [isMicrosoftSyncVisible, setIsMicrosoftSyncVisible] = useState(false);
    const [googleUsers, setGoogleUsers] = useState([]);
    const [microsoftUsers, setMicrosoftUsers] = useState([]);
    const [isMicrosoftTokenChecking, setIsMicrosoftTokenChecking] = useState(false);
    const [isGoogleTokenChecking, setIsGoogleTokenChecking] = useState(false);

    useEffect(() => {
        if (!token || !user?._id || !user?.email) return;

        const checkCalendarSyncConditions = async () => {
            try {
                // Microsoft Calendar Checks
                const isMicrosoftCalendarEnabled = await checkMicrosoftCalendarEnabledStatus(token, user._id);
                const microsoftTokenValidityData = await checkMicrosoftTokenValidity(token, user, setIsMicrosoftTokenChecking);
                const microsoftIntegrationData = await checkMicrosoftCalendarIntegration(token, user.email);

                const updatedMicrosoftUsers = microsoftIntegrationData?.users?.map(user => {
                    const tokenResult = microsoftTokenValidityData?.results?.find(
                        result => result?.syncedMicrosoftEmail === (user.syncedMicrosoftEmail || user.email || 'unknown')
                    );
                    return {
                        ...user,
                        isValid: tokenResult ? tokenResult.isValid : false,
                        tokenInfo: tokenResult && tokenResult.isValid ? tokenResult.tokenInfo : null,
                        tokenError: tokenResult && !tokenResult.isValid ? tokenResult.message : null,
                    };
                }) || [];

                setIsMicrosoftSyncVisible(
                    isMicrosoftCalendarEnabled &&
                    Array.isArray(microsoftTokenValidityData?.results) &&
                    microsoftTokenValidityData.results.some(result => result?.isValid) &&
                    microsoftIntegrationData?.exists
                );
                setMicrosoftUsers(updatedMicrosoftUsers);

                // Google Calendar Checks
                const isGoogleCalendarEnabled = await checkGoogleCalendarEnabledStatus(token, user._id);
                const tokenValidityData = await checkTokenValidity(token, user, setIsGoogleTokenChecking);
                const googleIntegrationData = await checkGoogleCalendarIntegration(token, user.email);

                const updatedGoogleUsers = googleIntegrationData?.users?.map(user => {
                    const tokenResult = tokenValidityData?.results?.find(
                        result => result?.syncedGoogleEmail === (user.syncedGoogleEmail || user.email || 'unknown')
                    );
                    return {
                        ...user,
                        isValid: tokenResult ? tokenResult.isValid : false,
                        tokenInfo: tokenResult && tokenResult.isValid ? tokenResult.tokenInfo : null,
                        tokenError: tokenResult && !tokenResult.isValid ? tokenResult.message : null,
                    };
                }) || [];

                setIsGoogleSyncVisible(
                    isGoogleCalendarEnabled &&
                    Array.isArray(tokenValidityData?.results) &&
                    tokenValidityData.results.some(result => result?.isValid) &&
                    googleIntegrationData?.exists
                );
                setGoogleUsers(updatedGoogleUsers);
            } catch (error) {
                console.error('Error checking calendar conditions:', error);
                setIsMicrosoftSyncVisible(false);
                setIsGoogleSyncVisible(false);
                setMicrosoftUsers([]);
                setGoogleUsers([]);
            }
        };

        checkCalendarSyncConditions();
    }, [token, user?._id, user?.email]);

    return {
        isGoogleSyncVisible,
        isMicrosoftSyncVisible,
        googleUsers,
        microsoftUsers,
        isMicrosoftTokenChecking,
        isGoogleTokenChecking,
        setIsMicrosoftTokenChecking,
        setIsGoogleTokenChecking,
    };
};

